import { useState } from "react";
import { MessagesSquare, ChevronDown, HelpCircle } from "lucide-react";
import { useLang, LANGS } from "@/context/LangContext";
import { useApi } from "@/lib/api";

const label = (t, key) => (key.startsWith("topic:") ? t(`topic_${key.slice(6)}`) : key);

function Topics({ data }) {
  const { t } = useLang();
  const max = Math.max(1, ...data.topics.map((x) => x.sessions));
  return (
    <div data-testid="inbox-topics" className="space-y-2">
      <p className="text-xs text-slate-500">{t("inbox_count").replace("{n}", data.total_questions).replace("{s}", data.sessions)}</p>
      {data.topics.map((x) => (
        <div key={x.key} data-testid={`inbox-topic-${x.key.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="text-sm">
          <div className="flex justify-between"><span className="font-medium text-slate-700">{label(t, x.key)}</span><span className="text-slate-500">{x.sessions} {t("inbox_visitors")}</span></div>
          <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#064E3B]" style={{ width: `${(x.sessions / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function Conversation({ c }) {
  const { t, sel } = useLang();
  const locale = LANGS.find((l) => l.code === sel)?.locale;
  const [open, setOpen] = useState(false);
  const { data } = useApi(open ? `/chat/${c.session_id}` : null);
  return (
    <li data-testid={`inbox-session-${c.session_id}`} className="rounded-2xl border border-slate-200 p-3">
      <button onClick={() => setOpen(!open)} data-testid={`inbox-session-toggle-${c.session_id}`} className="flex w-full items-start gap-3 text-left">
        <span className="mt-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">{c.lang || "id"}</span>
        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-slate-800">{c.first_q}</span>
          <span className="text-xs text-slate-500">{new Date(c.last).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })} · {c.messages} {t("inbox_msgs")}</span></span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">{(data || []).map((m, i) => (
        <p key={i} className={`whitespace-pre-wrap rounded-xl px-3 py-2 text-xs ${m.role === "user" ? "bg-[#064E3B] text-white" : "bg-slate-100 text-slate-700"}`}>{m.text}</p>
      ))}</div>}
    </li>
  );
}

function Unanswered() {
  const { t, sel } = useLang();
  const [days, setDays] = useState(7);
  const { data } = useApi(`/admin/chat-unanswered?days=${days}`);
  const locale = LANGS.find((l) => l.code === sel)?.locale;
  return (
    <div data-testid="inbox-unanswered" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 font-bold text-amber-900"><HelpCircle className="h-4 w-4" />{t("ua_title")} {data && `(${data.count})`}</p>
        <div className="ml-auto flex gap-1">{[[7, "ua_week"], [30, "ua_month"]].map(([d, k]) => (
          <button key={d} type="button" onClick={() => setDays(d)} data-testid={`inbox-unanswered-range-${d}`} className={`rounded-full px-3 py-1 text-xs font-bold ${days === d ? "bg-amber-900 text-white" : "bg-white text-amber-900"}`}>{t(k)}</button>
        ))}</div>
      </div>
      <p className="mt-1 text-xs text-amber-800">{t("ua_sub")}</p>
      {data?.count === 0 && <p data-testid="inbox-unanswered-empty" className="mt-3 text-sm text-amber-900/70">{t("ua_empty")}</p>}
      <ul className="mt-3 space-y-1.5">{(data?.questions || []).map((q, i) => (
        <li key={`${q.session_id}-${i}`} data-testid={`inbox-unanswered-item-${i}`} className="flex gap-2 rounded-xl bg-white px-3 py-2 text-sm">
          <span className="mt-0.5 rounded-full bg-amber-100 px-1.5 text-[10px] font-bold uppercase text-amber-800">{q.lang}</span>
          <span className="flex-1 text-slate-800">{q.text}</span>
          <span className="shrink-0 text-xs text-slate-400">{new Date(q.created_at).toLocaleDateString(locale, { dateStyle: "medium" })}</span>
        </li>
      ))}</ul>
    </div>
  );
}

export default function ChatInbox() {
  const { t } = useLang();
  const topics = useApi("/admin/chat-topics").data;
  const chats = useApi("/admin/chats").data;
  return (
    <div data-testid="admin-chat-inbox" className="mt-10 rounded-3xl border border-slate-200 bg-white p-5">
      <h2 className="flex items-center gap-2 text-xl font-bold"><MessagesSquare className="h-5 w-5 text-[#064E3B]" />{t("inbox_title")}</h2>
      <p className="text-sm text-slate-500">{t("inbox_sub")}</p>
      {chats && chats.length === 0 ? <p data-testid="inbox-empty" className="mt-4 text-sm text-slate-500">{t("inbox_empty")}</p> : (
        <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_1.4fr]">
          {topics && <Topics data={topics} />}
          <ul className="max-h-[480px] space-y-2 overflow-y-auto pr-1">{(chats || []).map((c) => <Conversation key={c.session_id} c={c} />)}</ul>
        </div>
      )}
      <Unanswered />
    </div>
  );
}
